import React, { useState } from 'react';
import { CamerasParamList } from '../../../typings/Params';
import { NavigationProp, RouteProp, useNavigation } from '@react-navigation/native';
import Filters from '../../../components/Filter';
import UserDropdown from './UserDropdown';
import StatusDropdown from './StatusDropdown';
import FilterDatePicker from '../../../components/Filter/components/FilterDateInput';
import { translate } from '../../../services/translate';
import moment from 'moment';
import { View } from 'react-native';
import FilterInput from '../../../components/Filter/components/FilterInput';

interface Props {
    filters: CamerasParamList['VideoAnalysis'];
    setFilters: React.Dispatch<React.SetStateAction<CamerasParamList['VideoAnalysis']>>;
    textFilter: string;
    setTextFilter: React.Dispatch<React.SetStateAction<string>>;
}

function Filter({ filters, setFilters, textFilter, setTextFilter }: Props) {
    const navigation = useNavigation<NavigationProp<RouteProp<CamerasParamList, 'VideoAnalysis'>>>();

    const [requesterUserId, setRequesterUserId] = useState<number | undefined>(filters.requesterUserId);
    const [status, setStatus] = useState<VideoAnalysisStatus | undefined>(filters.status as VideoAnalysisStatus);
    const [beginInDateFilter, setBeginInDateFilter] = useState(filters.startDate ? new Date(filters.startDate) : null);
    const [beginInHourFilter, setBeginInHourFilter] = useState(filters.startDate ? moment(filters.startDate).format('HH:mm') : '');
    const [endInDateFilter, setEndInDateFilter] = useState(filters.endDate ? new Date(filters.endDate) : null);
    const [endInHourFilter, setEndInHourFilter] = useState(filters.endDate ? moment(filters.endDate).format('HH:mm') : '');

    const [estimatedStartZIndex, setEstimatedStartZIndex] = useState<number>(3);
    const [estimatedFinishZIndex, setEstimatedFinishZIndex] = useState<number>(2);

    const handleCleanFilter = () => {
        setRequesterUserId(0);
        setStatus(undefined);
        setBeginInDateFilter(null);
        setBeginInHourFilter('');
        setEndInDateFilter(null);
        setEndInHourFilter('');
        setTextFilter('');
    };

    const handleFilter = () => {
        const formatDateTime = (date: Date | null, time: string) =>
            date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss').valueOf() : undefined;
        const beginIn = formatDateTime(beginInDateFilter, beginInHourFilter ? `${beginInHourFilter}:00` : '00:00:00');
        const endIn = formatDateTime(endInDateFilter, endInHourFilter ? `${endInHourFilter}:59` : '23:59:59');

        navigation.setParams({
            ...filters,
            requesterUserId: requesterUserId != 0 ? requesterUserId : undefined,
            status: status !== undefined ? status : undefined,
            startDate: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endDate: endIn && !isNaN(endIn) ? endIn : undefined,
            textFilter: textFilter !== '' ? textFilter : undefined,
            page: 1,
        });

        setFilters({
            ...filters,
            requesterUserId: requesterUserId != 0 ? requesterUserId : undefined,
            status: status !== undefined ? status : undefined,
            startDate: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endDate: endIn && !isNaN(endIn) ? endIn : undefined,
            textFilter: textFilter !== '' ? textFilter : undefined,
            page: 0,
        });
    };

    return (
        <Filters onCleanFilters={handleCleanFilter} onFilters={handleFilter}>
            <FilterInput
                label={translate('typeToSearch')}
                value={textFilter}
                placeholder={translate('typeToSearch')}
                onChange={setTextFilter}
            />
            <StatusDropdown
                value={status}
                onChange={status => {
                    setStatus(status as VideoAnalysisStatus);
                }}
                zIndex={5}
            />
            <UserDropdown
                value={requesterUserId}
                onChange={user => {
                    setRequesterUserId(+user);
                }}
                zIndex={4}
            />
            <View style={{ zIndex: estimatedStartZIndex }}>
                <FilterDatePicker
                    date={beginInDateFilter}
                    onChangeDate={setBeginInDateFilter}
                    time={beginInHourFilter}
                    onChangeTime={setBeginInHourFilter}
                    label={translate('createdAt')}
                    zIndex={3}
                    onCalendarOpen={() => setEstimatedStartZIndex(99999)}
                    onCalendarClose={() => setEstimatedStartZIndex(3)}
                />
            </View>
            <View style={{ zIndex: estimatedFinishZIndex }}>
                <FilterDatePicker
                    date={endInDateFilter}
                    onChangeDate={setEndInDateFilter}
                    time={endInHourFilter}
                    onChangeTime={setEndInHourFilter}
                    label={translate('endIn')}
                    zIndex={2}
                    onCalendarOpen={() => setEstimatedFinishZIndex(99999)}
                    onCalendarClose={() => setEstimatedFinishZIndex(2)}
                />
            </View>
        </Filters>
    );
}

export default Filter;
